/**
 * Zod shape of one canonical record per form, plus its JSON Schema for the public API.
 *
 * The vocabulary is the upload-template column set (see formFields.ts), which is the same
 * vocabulary the per-form generators read. Everything is an optional string except the few
 * fields a filing genuinely cannot be built without; amounts are strings that must parse as
 * numbers, because that is how they arrive from CSV and from the portal alike.
 *
 * 1099-NEC is complete and hand-checked. The other seventeen forms use a permissive schema
 * built from their interface keys: shape errors there are advisory, and checkRecords (which
 * covers all eighteen forms) is the gate that actually decides whether a record is fileable.
 */
import { z, ZodTypeAny } from "zod";
import zodToJsonSchema from "zod-to-json-schema";
import { FORM_FIELDS } from "./formFields";
import { fieldRules } from "./fieldRules";
import type { FormType } from "../context";

/** A money column: optional, but if present it has to be a number we can add up. */
export const amountString = z
  .union([z.string(), z.number()])
  .optional()
  .refine(
    (v) => v === undefined || v === null || String(v).trim() === "" ||
      Number.isFinite(Number(String(v).replace(/[$,]/g, ""))),
    { message: "must be a number" }
  );

const optionalText = z.union([z.string(), z.number()]).optional();

/** Every record TabFile files carries the customer's own stable reference. */
const recordRef = z.string().min(1, "recordRef is required");

/**
 * Which columns hold money, and which hold something that merely sits in a box.
 *
 * core-api already answers this, per form and per tax year, in
 * `src/utils/taxFormsStructures/{year}/fields{FORM}_{year}.ts`: a money column is validated
 * with isNonNegativeDecimal and marked isBoxTotal. Those definitions are extracted into
 * fieldRules.generated.ts and read through fieldRules.ts, with the hand-written rules for
 * e-file-app-only forms (fieldRules.manual.ts) merged in.
 *
 * A name-shaped guess is what used to tell a customer that BOX8_FOREIGN_COUNTRY "must be a
 * number (got \"CANADA\")": a box number says where a value is printed, not what it is.
 */
function amountFields(formType: string, taxYear: number): Set<string> {
  const rules = fieldRules(formType, taxYear);
  const out = new Set<string>();
  for (const [field, rule] of Object.entries(rules)) {
    if (rule.kind === "amount") out.add(field);
  }
  return out;
}

/** The spellings of a ticked checkbox that reach us: X from a template, 1 from Pub 1220, and
 *  the words a JSON client is likely to send. */
const CHECKED = /^(X|1|Y|YES|TRUE)$/i;

/**
 * One checkbox value, as it is stored: "X" when ticked, null when not.
 *
 * Every reader of a canonical record agrees on "X" - the Pub 1220 intake writes it, the
 * 1099-B builder compares against it, and the builders that call `isTrue` accept it. A
 * boolean `true` from the record editor satisfied none of the ones comparing strings, and a
 * bare `false` was neither blank nor X. So the boundary turns whatever it was handed into
 * the one spelling the rest of the engine reads.
 */
export function canonicalCheckbox(value: unknown): "X" | null {
  if (value === true) return "X";
  if (value === false || value === null || value === undefined) return null;
  const text = String(value).trim();
  if (text === "" || text === "0") return null;
  return CHECKED.test(text) ? "X" : null;
}

/** True for a value `canonicalCheckbox` would accept, blank included. */
function isCheckboxValue(v: unknown): boolean {
  if (typeof v === "boolean" || v === undefined || v === null) return true;
  const text = String(v).trim();
  return text === "" || text === "0" || CHECKED.test(text);
}

/** An X checkbox, per core-api: blank, or X. A boolean is read as ticked or not. */
const checkboxString = z
  .union([z.string(), z.number(), z.boolean()])
  .nullish()
  .refine(isCheckboxValue, { message: "must be X or blank" });

/**
 * The record with each checkbox field of the form written as "X" or null.
 *
 * Applied where a record is accepted from outside - the record editor, a JSON client - so
 * what is stored reads the same as what the file intake stores. Fields the rules do not
 * call a checkbox are left exactly as they were.
 */
export function normalizeCheckboxes(
  formType: string,
  taxYear: number,
  record: Record<string, unknown>
): Record<string, unknown> {
  const rules = fieldRules(formType, taxYear);
  const out: Record<string, unknown> = { ...record };
  for (const [field, rule] of Object.entries(rules)) {
    if (rule.kind !== "checkbox" || !(field in out)) continue;
    out[field] = canonicalCheckbox(out[field]);
  }
  return out;
}

const NEC_SCHEMA = z
  .object({
    recordRef,
    id: z.union([z.string(), z.number()]).optional(),

    FILER_FEDERAL_ID_NUMBER: z.string().min(1, "the payer's federal ID number is required"),
    FILER_NAME: optionalText,
    FILER_NAME2: optionalText,
    FILER_ADDRESS1: optionalText,
    FILER_ADDRESS2: optionalText,
    FILER_CITY: optionalText,
    FILER_STATE: optionalText,
    FILER_ZIP: optionalText,
    FILER_PHONE: optionalText,
    FILER_SUITE_NO: optionalText,
    FILER_COUNTRY: optionalText,

    RECIPIENT_ID_NUMBER: z.string().min(1, "the recipient's TIN is required"),
    RECIPIENT_ID_TYPE: optionalText,
    RECIPIENT_NAME: optionalText,
    RECIPIENT_NAME2: optionalText,
    RECIPIENT_FIRST_NAME: optionalText,
    RECIPIENT_MIDDLE_NAME: optionalText,
    RECIPIENT_LAST_NAME: optionalText,
    RECIPIENT_SUFFIX: optionalText,
    RECIPIENT_BUSINESS_NAME1: optionalText,
    RECIPIENT_BUSINESS_NAME2: optionalText,
    RECIPIENT_ADDRESS1: optionalText,
    RECIPIENT_ADDRESS2: optionalText,
    RECIPIENT_ADDRESS3: optionalText,
    RECIPIENT_APT_NO: optionalText,
    RECIPIENT_CITY: optionalText,
    RECIPIENT_STATE: optionalText,
    RECIPIENT_ZIP: optionalText,
    FOREIGN_COUNTRY: optionalText,
    ACCOUNT_NUMBER: z.string().max(30, "account number must not exceed 30 characters").optional(),

    BOX1_NONEMPLOYEE_COMPENSATION: amountString,
    BOX1A_NONEMPLOYEE_COMPENSATION: amountString,
    BOX1B_CASH_TIPS: amountString,
    BOX1C_TTOC1: optionalText,
    BOX1C_TTOC2: optionalText,
    BOX1D_OVERTIME_COMPENSATION: amountString,
    BOX2_DIRECT_SALE: optionalText,
    BOX2_DIRECT_SALES: optionalText,
    BOX3_GOLDEN_PARACHUTE: amountString,
    BOX4_FED_INCOME_TAX_WITHHELD: amountString,
    BOX5_STATE_TAX_WITHHELD1: amountString,
    BOX5_STATE_TAX_WITHHELD2: amountString,
    BOX6_PAYER_STATE_NUMBER1: optionalText,
    BOX6_PAYER_STATE_NUMBER2: optionalText,
    BOX7_STATE_INCOME1: amountString,
    BOX7_STATE_INCOME2: amountString,
    FATCA_REQUIREMENT: optionalText,

    "1099_YEAR": z.union([z.string(), z.number()]).optional(),
    YEAR: z.union([z.string(), z.number()]).optional(),
    corrected: z.boolean().optional(),
  })
  .passthrough();

function builtSchema(formType: string, taxYear: number): ZodTypeAny {
  const rules = fieldRules(formType, taxYear);
  const shape: Record<string, ZodTypeAny> = { recordRef };
  const fields = new Set([...(FORM_FIELDS[formType] || []), ...Object.keys(rules)]);
  for (const field of fields) {
    if (field === "recordRef") continue;
    const kind = rules[field]?.kind;
    shape[field] =
      kind === "amount" ? amountString : kind === "checkbox" ? checkboxString : optionalText;
  }
  return z.object(shape).passthrough();
}

const cache = new Map<string, ZodTypeAny>();

/** The newest year core-api defines, used when a caller does not name one. */
export const DEFAULT_RULES_YEAR = 2026;

/**
 * Zod schema of one canonical record for the form, in one tax year.
 *
 * 1099-NEC keeps its hand-written identity requirements (the filer TIN and recipient TIN
 * messages a customer actually reads) and merges the generated field kinds over the top, so
 * the two can no longer disagree about which NEC column is money.
 */
export function recordSchema(
  formType: FormType | string,
  taxYear: number = DEFAULT_RULES_YEAR
): ZodTypeAny {
  const key = `${String(formType)}@${taxYear}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const built = builtSchema(String(formType), taxYear);
  const schema =
    String(formType) === "1099-NEC"
      ? (built as any).merge(NEC_SCHEMA.pick({
          recordRef: true,
          FILER_FEDERAL_ID_NUMBER: true,
          RECIPIENT_ID_NUMBER: true,
          ACCOUNT_NUMBER: true,
        } as any))
      : built;
  cache.set(key, schema);
  return schema;
}

/** JSON Schema (draft 7) of one canonical record, for the API's /v1/forms/{form}/schema. */
export function recordJsonSchema(
  formType: FormType | string,
  taxYear: number = DEFAULT_RULES_YEAR
): Record<string, unknown> {
  return zodToJsonSchema(recordSchema(formType, taxYear) as any, {
    name: `${String(formType)}Record`,
    target: "jsonSchema7",
  }) as Record<string, unknown>;
}
